import React from "react";
import {observer} from "mobx-react-lite";
import {Alert, Autocomplete, Checkbox, TextField} from "@mui/material";
import {useSitStores} from "../../../../stores";
import type {Category, Cluster, Company, Division} from "../../../ApiTypes";
import {category_readable_str} from "../../../ApiTypes";
import {SitField, sitFormTags} from "../../../components/design/SitDesignAtoms";

export const InitiativeScopeForm: React.FC = observer(() => {
    const {ccStore, initiativeInstanceStore: s} = useSitStores();
    const fc = s.f_c[0];
    const dis = s.disabled;

    return (
        <>
            {s.w_saving_split_removed_companies && <Alert severity="warning" color="error" className="mb-2">
                Initiative savings split has companies that are not in scope:&nbsp;
                {s.w_saving_split_removed_companies.map((c) => ccStore.getCompanyTitle(c)).join(', ')}
            </Alert>}
            {s.w_saving_split_removed_categories && <Alert severity="warning" color="error" className="mb-2">
                Initiative savings split has categories that are not in scope:&nbsp;
                {s.w_saving_split_removed_categories.map((c) => ccStore.getCategoryReadableStr(c)).join(', ')}
            </Alert>}

            <div className="sit-grid">
                <label className="sitf">
                    <span className="sitf-label">Company</span>
                    <Autocomplete
                        disabled={dis} multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                        size="small" limitTags={1} renderTags={sitFormTags((o: {title: string}) => o.title)}
                        options={ccStore.companyRM.result ?? ([] as Company[])}
                        getOptionLabel={(o) => o.title}
                        value={fc.companyValues}
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="All"/>}
                        renderOption={(props, option) =>
                            <li {...props} key={option.id}>
                                <Checkbox checked={(fc.instance.companies?.indexOf(option.id) ?? -1) > -1}/>
                                {option.title}
                            </li>}
                        onChange={(_e, value) => fc.companies = value.map((c) => c.id)}/>
                </label>

                <label className="sitf">
                    <span className="sitf-label">Cluster</span>
                    <Autocomplete
                        disabled={dis} multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                        size="small" limitTags={1} renderTags={sitFormTags((o: {title: string}) => o.title)}
                        options={ccStore.clusterRM.result ?? ([] as Cluster[])}
                        getOptionLabel={(o) => o.title}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={fc.combinedClusters}
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="All"/>}
                        onChange={(_e, value, reason) => fc.setClusters(value, reason === 'removeOption')}/>
                </label>

                <label className="sitf">
                    <span className="sitf-label">Division</span>
                    <Autocomplete
                        disabled={dis} multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                        size="small" limitTags={1} renderTags={sitFormTags((o: {title: string}) => o.title)}
                        options={ccStore.divisionRM.result ?? ([] as Division[])}
                        getOptionLabel={(o) => o.title}
                        value={fc.divisionValues}
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="All"/>}
                        onChange={(_e, value) => fc.divisions = value.map((d) => d.id)}/>
                </label>

                <SitField label="Scope">
                    <select value={s.instance.local ? 1 : 2} disabled={dis}
                            onChange={e => s.local = Number(e.target.value) === 1}>
                        <option value={1}>Local</option>
                        <option value={2}>Global</option>
                    </select>
                </SitField>

                <label className="sitf">
                    <span className="sitf-label">Category</span>
                    <Autocomplete
                        disabled={dis} multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                        size="small" limitTags={1} renderTags={sitFormTags(category_readable_str)}
                        options={ccStore.categoryRM.result ?? ([] as Category[])}
                        getOptionLabel={(o) => category_readable_str(o)}
                        value={fc.categoryValues}
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="All"/>}
                        onChange={(_e, value) => fc.categories = value.map((c) => c.id)}/>
                </label>
            </div>
        </>
    );
});
